import { type ActionEvent } from "@hotwired/stimulus"

import DrawerController from "../drawer_controller"
import DrawerGroupController from "../drawer_group_controller"
import ModalController from "../modal_controller"
import SearchDateController from "./search_date_controller"
import SearchExperienceController from "./search_experience_controller"
import SearchBudgetController from "@/controllers/search/search_budget_controller"
import SearchGuestsController from "@/controllers/search/search_guests_controller"

import { saveNavigationTypeToStorage, selectContentEvent } from "@/helpers/ga4_helper"
import { setInputValueWithEvents } from "@/helpers/utils"

const GIFTING_JOURNEY = document.body.getAttribute("data-gifting-journey") === "true"

export default class extends ModalController {
  static outlets = [
    "drawer",
    "drawer-group",
    "search-budget",
    "search-date",
    "search-experience",
    "search-guests"
  ]
  declare readonly drawerOutlets: DrawerController[]
  declare readonly drawerGroupOutlet: DrawerGroupController
  declare readonly searchBudgetOutlet: SearchBudgetController
  declare readonly hasSearchBudgetOutlet: boolean // GIFTING_JOURNEY: Remove when experiment done (outlet always exist)
  declare readonly searchDateOutlet: SearchDateController
  declare readonly searchExperienceOutlet: SearchExperienceController
  declare readonly searchGuestsOutlet: SearchGuestsController

  static targets = [
    "budgetSummary",
    "calendarContainer",
    "calendarHeader",
    "calendarMonth",
    "clearAllButton",
    "dateRadio",
    "experienceSummary",
    "guestsSummary",
    "journeyRadio",
    "locationInput",
    "locationSummary"
  ]

  declare readonly budgetSummaryTarget: HTMLElement
  declare readonly calendarContainerTarget: HTMLElement
  declare readonly calendarHeaderTarget: HTMLElement
  declare readonly calendarMonthTargets: HTMLTableElement[]
  declare readonly clearAllButtonTarget: HTMLElement
  declare readonly dateRadioTargets: HTMLInputElement[]
  declare readonly experienceSummaryTarget: HTMLElement
  declare readonly guestsSummaryTarget: HTMLElement
  declare readonly journeyRadioTargets: HTMLInputElement[]
  declare readonly locationInputTarget: HTMLInputElement
  declare readonly locationSummaryTarget: HTMLElement

  static values = {
    shouldTrackHistory: { type: Boolean, default: true }
  }
  declare readonly shouldTrackHistoryValue: boolean

  connect(): void {
    super.connect()

    if (GIFTING_JOURNEY) {
      // Inputs disabled by default until controller loaded.
      this.journeyRadioTargets.forEach((input) => (input.disabled = false))
    }
  }

  clearAll(): void {
    setInputValueWithEvents(this.locationInputTarget, "")
    this.searchDateOutlet.deselectAllDates()
    this.searchExperienceOutlet.resetExperience()
    this.searchGuestsOutlet.resetGuests()
    if (this.hasSearchBudgetOutlet) {
      this.searchBudgetOutlet.resetBudget()
    }
    this.drawerOutlets.forEach((drawer) => drawer.close())
    this.clearAllButtonTarget.hidden = true
  }

  protected performOpen(shouldAnimate: boolean): void {
    super.performOpen(shouldAnimate)

    // Set custom prop for calendar scroll offset equal to height of sticky header, plus gap.
    this.element.style.setProperty(
      "--search-modal-date-offset",
      `${this.calendarHeaderTarget.getBoundingClientRect().height + 16}px`
    )

    if (shouldAnimate) {
      this.updateClearAllVisibility()
    }

    // Open first drawer that's missing a value.
    if (!this.currentOpenDrawer) {
      this.openNextEmptyDrawer()
    }
  }

  openDateDrawer(): void {
    // Select appropriate tab and scroll to selected date before opening drawer to reduce jumping.
    this.searchDateOutlet.selectCalendarTab()
    this.jumpToSelectedDate()
    this.openDrawer(this.dateDrawer)
  }

  openExperienceDrawer(): void {
    this.openDrawer(this.experienceDrawer)
  }

  openGuestsDrawer(): void {
    this.openDrawer(this.guestsDrawer)
  }

  openBudgetDrawer(): void {
    this.openDrawer(this.budgetDrawer)
  }

  openLocationDrawer(): void {
    this.openDrawer(this.locationDrawer)
  }

  toggleJourney(): void {
    this.searchExperienceOutlet.resetExperienceSelection()

    if (this.giftJourneyValue) {
      this.searchDateOutlet.deselectAllDates()
      this.searchGuestsOutlet.resetGuests()
      this.setGuestsSummary()
      selectContentEvent("Search Bar", "Buy as a gift voucher")
    } else {
      this.searchBudgetOutlet.resetBudget()
      this.setBudgetSummary()
      selectContentEvent("Search Bar", "Book a spa")
    }

    this.updateClearAllVisibility()
  }

  openNextEmptyDrawer(): void {
    const { currentOpenDrawer, giftJourneyValue } = this

    const hasLocation = !!this.locationInputTarget.value
    const hasDate = this.searchDateOutlet.isDateSelected
    const hasExperience = !this.searchExperienceOutlet.isDefaultSelected

    const followingDrawers = currentOpenDrawer
      ? this.drawerOutlets.filter(
          (drawer) =>
            currentOpenDrawer.element.compareDocumentPosition(drawer.element) &
            Node.DOCUMENT_POSITION_FOLLOWING
        )
      : this.drawerOutlets

    if (!hasLocation && followingDrawers.includes(this.locationDrawer)) {
      this.openLocationDrawer()
    } else if (!hasDate && followingDrawers.includes(this.dateDrawer) && !giftJourneyValue) {
      this.openDateDrawer()
    } else if (!hasExperience && followingDrawers.includes(this.experienceDrawer)) {
      this.openExperienceDrawer()
    } else if (!giftJourneyValue && followingDrawers.includes(this.guestsDrawer)) {
      this.openGuestsDrawer()
    } else if (
      giftJourneyValue &&
      this.hasSearchBudgetOutlet &&
      followingDrawers.includes(this.budgetDrawer)
    ) {
      this.openBudgetDrawer()
    }
  }

  setExperienceSummary({ params: { name } }: ActionEvent): void {
    this.experienceSummaryTarget.textContent = name
    this.updateClearAllVisibility()
  }

  setGuestsSummary(): void {
    this.guestsSummaryTarget.textContent = this.searchGuestsOutlet.summaryText
    this.updateClearAllVisibility()
  }

  setBudgetSummary(): void {
    if (this.hasSearchBudgetOutlet) {
      this.budgetSummaryTarget.textContent = this.searchBudgetOutlet.summaryText
      this.updateClearAllVisibility()
    }
  }

  setLocationSummary({ currentTarget }: ActionEvent): void {
    this.locationSummaryTarget.textContent = (currentTarget as HTMLInputElement).value || "Any"
    this.updateClearAllVisibility()
  }

  submit(): void {
    saveNavigationTypeToStorage("search_box")
  }

  updateClearAllVisibility(): void {
    const { giftJourneyValue } = this
    const hasLocation = !!this.locationInputTarget.value
    const hasDate = !giftJourneyValue && this.searchDateOutlet.isDateSelected
    const hasExperience = !this.searchExperienceOutlet.isDefaultSelected
    const hasGuests = !giftJourneyValue && !this.searchGuestsOutlet.isZeroSelected
    const hasBudget =
      giftJourneyValue && this.hasSearchBudgetOutlet && !this.searchBudgetOutlet.isDefaultSelected

    const selectedCount = [hasLocation, hasDate, hasExperience, hasGuests, hasBudget].filter(
      Boolean
    ).length
    this.clearAllButtonTarget.hidden = selectedCount < 2
  }

  private get giftJourneyValue(): boolean {
    return this.journeyRadioTargets.some((input) => input.checked && input.value === "gift")
  }

  private get currentOpenDrawer(): DrawerController | undefined {
    return this.drawerOutlets.find((drawer) => drawer.openValue)
  }

  private get dateDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-date]")
  }

  private drawerFor(searchInputControllerSelector: string): DrawerController {
    return this.drawerOutlets.find(
      (drawer) =>
        !!drawer.element.querySelector(searchInputControllerSelector) ||
        !!drawer.element.closest(searchInputControllerSelector)
    )!
  }

  private get experienceDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-experience]")
  }

  private get budgetDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-budget]")
  }

  private get guestsDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-guests]")
  }

  private jumpToSelectedDate(): void {
    const month = this.selectedDateRadio
      ? this.calendarMonthTargets.find((month) => month.contains(this.selectedDateRadio!))
      : this.calendarMonthTargets.at(0)

    // We use `scrollTop` on parent instead of `scrollIntoView` on element because the latter
    // will try to scroll ancestor containers rather than the immediate parent, and we can't
    // hide overflow on those ancestors without breaking the auto drawer height that fills
    // the available modal space.
    this.calendarContainerTarget.scrollTop =
      month!.offsetTop - this.calendarContainerTarget.offsetTop
  }

  private get locationDrawer(): DrawerController {
    return this.drawerFor("[data-controller*=search-location]")
  }

  private openDrawer(drawer: DrawerController): void {
    // Set custom prop for drawer height offset equal to height of previous drawers, plus padding.
    const PADDING = 8
    const drawerGap = parseInt(
      window.getComputedStyle(this.drawerGroupOutlet.element).getPropertyValue("gap")
    )

    // Limit to 3 because at most four drawers are visible. In the gifting journey,
    // the budget drawer is the third visible drawer, even though it's fifth in the DOM.
    const drawerIndex = Math.min(this.drawerOutlets.indexOf(drawer), 3)
    const offset = drawer.element.getBoundingClientRect().height + drawerGap
    let value = offset * drawerIndex + PADDING

    if (GIFTING_JOURNEY) {
      const TOGGLE_BUTTONS_HEIGHT = 50

      value += TOGGLE_BUTTONS_HEIGHT
    }

    this.element.style.setProperty("--search-modal-height-offset", `${value}px`)

    drawer.open()
  }

  private get selectedDateRadio(): HTMLInputElement | undefined {
    return this.dateRadioTargets.find((radio) => radio.checked)
  }
}
